import {
  HttpClient,
  HttpContext,
  HttpContextToken
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';

import { InsuranceCertificate, InsurancePolicy } from '../classes/models';
import { InsuranceAgent } from '../classes/models/insurance-agent';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class InsuranceService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  saveInsuranceAgent(InsuranceAgent: InsuranceAgent): Observable<any> {
    return this.http.post(
      `/api/v2/vendor/${InsuranceAgent.VendorId}/insurance/agent`,
      InsuranceAgent
    );
  }

  saveInsuranceCertificate(
    InsuranceCertificate: InsuranceCertificate,
    query = {}
  ) {
    return this.http.post<InsuranceCertificate>(
      `/api/v2/admin/insurance/certificate`,
      InsuranceCertificate,
      { params: query }
    );
  }

  deleteInsuranceCertificate(query = {}) {
    return this.http.delete<InsurancePolicy>(
      `/api/v2/admin/insurance/insuranceCertificate/one`,
      { params: query }
    );
  }

  saveInsurancePolicy(InsurancePolicy: InsurancePolicy, query = {}) {
    return this.http.post<InsurancePolicy>(
      `/api/v2/admin/insurance/policy`,
      InsurancePolicy,
      { params: query }
    );
  }

  archiveInsuranceAgent(id: number, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.delete(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/insurance/agent/${id}/archive`,
              { params: query }
            ),
          Admin: () =>
            this.http.delete(`/api/v2/admin/insurance/agent/${id}/archive`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid Permission');
        throw new Error('Invalid Permission');
      })
    );
  }

  public getInsurancePoliciesCount(
    query: {} = {}
  ): Observable<InsurancePolicy[]> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get<InsurancePolicy[]>(
              `/api/v2/admin/insurance/countInsurancePolicies`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid Permission');
        throw new Error('Invalid Permission');
      })
    );
  }

  getInsuranceCertificates(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get<InsuranceCertificate[]>(
              `/api/v2/admin/insurance/insuranceCertificate/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid Permission');
        throw new Error('Invalid Permission');
      })
    );
  }

  getInsuranceCertificate(
    query = {},
    optional: { skipLoading?: boolean } = {}
  ) {
    const { skipLoading } = optional;
    const context = new HttpContext();
    if (skipLoading) {
      const ContextToken = new HttpContextToken(() => 'SkipLoadingMask');
      context.set(ContextToken, null);
    }

    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get<InsuranceCertificate>(
              `/api/v2/admin/insurance/insuranceCertificate/one`,
              { params: query, context }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid Permission', {
          returnError: true
        });
      })
    );
  }

  getInsuranceCertificateCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get<number>(
              `/api/v2/admin/insurance/insuranceCertificate/count`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid Permission');
        throw new Error('Invalid Permission');
      })
    );
  }
}
